import { addLineItems, getCart } from '@/cart/cart-store'
import { endDrawer } from '@/components/drawers'
import { getLocalizedStrings } from '@/localization/localization-store'
import { ProductState, VariantState } from '@/product/types'
import { createShopifyImageUrl } from '@/utilities/create-shopify-image-url'
import formatCurrency from '@/utilities/format-currency'
import { handleize } from '@/utilities/handleize'
import { logError } from '@/utilities/log'
import classNames from 'classnames'
import { ChangeEvent } from 'preact/compat'
import { useState } from 'preact/hooks'
import { useEffect, useRef } from 'react'

/**
 * Props for the CollectionBuyStack component
 */
export type CollectionBuyStackProps = {
  /**
   * The product data
   */
  product: ProductState
}

/**
 * A component that displays a product image, title, price, and add to cart button
 * @param product - The product data
 * @returns A CollectionBuyStack component
 */
export default function CollectionBuyStack({ product }: CollectionBuyStackProps) {
  const [selectedVariant, setSelectedVariant] = useState<VariantState | null>(
    product.variants.find(({ available }) => available) ?? null
  )
  const cart = getCart()
  const currency = cart?.currency ?? 'USD'
  const price = selectedVariant?.price ?? 0
  const comparePrice = selectedVariant?.compareAtPrice ?? price
  const buttonLabel = getLocalizedStrings().collectionCtaButtonLabel ?? 'Add To Cart'
  const soldOutLabel = getLocalizedStrings().buyStackSoldOut ?? 'Sold Out'

  /**
   * Handles the change event for the variant select element
   * Updates the selected variant based on the selected value
   */
  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newVariant = product.variants.find(({ id }) => id === event.currentTarget?.value)
    if (!newVariant) return

    setSelectedVariant(newVariant)
  }

  /**
   * Handles the click event for the add to cart button.
   * Opens the end drawer and adds the selected variant to the cart
   * Logs an error if the operation fails
   * @returns A promise that resolves when the operation is complete
   */
  const handleClick = async () => {
    if (!selectedVariant) return

    try {
      endDrawer.open()
      await addLineItems([
        {
          id: parseInt(selectedVariant.id),
          quantity: 1
        }
      ])
    } catch (error) {
      logError(error)
    }
  }

  const media = product.collectionMedia
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    // On mobile, the video should start playing when it enters the viewport,
    // and pause when the user scrolls past it.
    const isMobileWidth = window.innerWidth < 640

    if (!isMobileWidth || !videoRef.current) return

    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          void videoRef.current!.play()
        } else {
          void videoRef.current!.pause()
        }
        break
      }
    })

    observer.observe(videoRef.current)

    return () => observer.disconnect()
  })

  return (
    <div className='grid grid-cols-1 gap-y-2'>
      <a href={product.url ?? ''}>
        {media?.mediaType === 'video' ? (
          // On desktop, the video should play only while the mouse is hovering over it.
          <video
            ref={videoRef}
            onMouseEnter={() => void videoRef.current?.play()}
            onMouseLeave={() => void videoRef.current?.pause()}
            playsInline
            muted
            loop
            alt={product.title}
            className='aspect-7/10 max-h-80 w-full object-contain'>
            {media.sources?.map(s => <source key={s} src={s}></source>)}
          </video>
        ) : (
          <div className='group relative aspect-7/10 max-h-80 w-full overflow-hidden'>
            <img
              src={createShopifyImageUrl(
                media?.url ?? selectedVariant?.image?.url ?? product.featuredImage?.url ?? '',
                440
              )}
              alt={product.title}
              className='aspect-7/10 max-h-80 w-full object-contain transition-transform duration-300 ease-in-out hover:scale-105'
            />
          </div>
        )}
      </a>
      <div className='md:min-h-16'>
        <a href={product.url ?? ''} className='text-deep-ocean-blue no-underline'>
          <h6 className='leading-tight'>{product.title}</h6>
        </a>
      </div>

      <span className='text-lg '>
        {!!comparePrice && comparePrice !== price && (
          <s className='mr-1 font-bold text-slate-400'>
            {formatCurrency(comparePrice, currency)}
          </s>
        )}
        <span className={!!comparePrice && comparePrice !== price ? 'text-error-red' : ''}>
          {formatCurrency(price, currency)}
        </span>
      </span>
      <div className='flex flex-col gap-y-2'>
        <div
          className={classNames({
            'pointer-events-none hidden opacity-0 md:block': product.variants.length <= 1
          })}>
          {product.variants.length > 0 && (
            <select
              aria-label={`${product.title} Variants`}
              className='w-full py-3'
              id={`${handleize(product.title)}-variants`}
              name={`${product.title} Variants`}
              onChange={handleChange}>
              {product.variants.map(({ id, title, available }) => (
                <option key={id} value={id} disabled={!available}>
                  {title}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          className='btn btn-square btn-sm w-full font-bold uppercase'
          disabled={!selectedVariant?.available}
          onClick={() => void handleClick()}>
          {selectedVariant?.available ? buttonLabel : soldOutLabel}
        </button>
      </div>
    </div>
  )
}
